/**
 * Initializes apollo.
 *
 * Creates the apollo client and sets authentication
 * headers for apollo link. Adapted from the withApollo example in zeit
 * https://github.com/zeit/next.js/tree/canary/examples/with-apollo
 */

import { ApolloClient, ApolloLink } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { setContext } from '@apollo/client/link/context';
import { InMemoryCache } from '@apollo/client/cache';
import { createUploadLink } from 'apollo-upload-client';
// @ts-ignore
import requestIdleCallback from 'requestidlecallback-polyfill';
import { useMemo } from 'react';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { getServerFrontendConfig } from '~/jobiqo/lib/frontendConfig';
import { getFrontend } from '~/jobiqo/utils/getFrontend';
import { getToken, isAnonymousByHeaders } from '~/jobiqo/lib/auth';

let apolloClient;
export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

if (process.browser && typeof window !== 'undefined') {
  // see https://gist.github.com/paullewis/55efe5d6f05434a96c36
  // Polyfill requestidle callback
  window.requestIdleCallback =
    window.requestIdleCallback ||
    function (cb) {
      return setTimeout(function () {
        const start = Date.now();
        cb({
          didTimeout: false,
          timeRemaining() {
            return Math.max(0, 50 - (Date.now() - start));
          },
        });
      }, 1);
    };

  // @ts-ignore
  window.cancelIdleCallback =
    window.cancelIdleCallback ||
    function (id) {
      clearTimeout(id);
    };
}

// rough first draft, could probably be optimised in a loads of different ways.
const hasFiles = (node, found: any[] = []) => {
  Object.keys(node).forEach((key) => {
    if (
      !(typeof node[key] === 'object' && node[key] !== null) ||
      found.length > 0
    ) {
      return;
    }

    if (
      (typeof File !== 'undefined' && node[key] instanceof File) ||
      (typeof Blob !== 'undefined' && node[key] instanceof Blob)
    ) {
      found.push(node[key]);
      return;
    }

    hasFiles(node[key], found);
  });

  return found.length > 0;
};

function create(initialState, ctx, session = null) {
  const URL = (process as any).env.graphqlEndpoint;

  const batchLink = new BatchHttpLink({
    uri: URL,
  });

  const authLink = setContext(async (_, { headers }) => {
    const isProduction = process.env.NODE_ENV === 'production';
    let token;

    if ((session as any)?.jwt) {
      // We have a session, we can get the jwt token from it.
      token = (session as any)?.jwt;
    } else {
      if (!isAnonymousByHeaders(ctx.req)) {
        // Only get token from session if we don't already have the jwt and user
        // is logged in.
        // We can tell by the cookies we don't need to call session.
        token = await getToken(ctx);
      }
    }

    // Production uses a different authorization header for the jwt.
    const jwtAuthHeader = isProduction ? 'Authorization' : 'JWT-Authorization';

    // Initialize headers if undefined.
    headers = {
      ...headers,
    };

    if (token) {
      headers[jwtAuthHeader] = token ? `Bearer ${token}` : '';
    }

    if (!isProduction) {
      headers['Authorization'] = 'Basic ' + process.env.BASIC_AUTH;
    }

    // Put the current frontend header for graphql requests.
    const frontendConfig = ctx?.req
      ? getServerFrontendConfig(ctx)
      : getCurrentFrontendConfig();
    const frontend = getFrontend(frontendConfig);
    if (frontend) {
      // If its default means the setting was not set in the frontend and so
      // we just don't send it.
      headers['jobiqo-frontend'] = `frontend=${frontend}; `;
      // Set a global header for the locale the user is in. This way
      //we can avoid having to pass locale information to all queries and mutations.

      if (ctx?.locale) {
        headers['jobiqo-frontend'] += `locale=${ctx.locale}`;
      }
      // Pass a visual editor param to the queries.
      if (ctx?.query?.visual_editor === 'true') {
        headers['visual-editor'] = ctx?.query?.visual_editor;
      } else {
        if (typeof window !== 'undefined') {
          // Get the param from the url
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get('visual_editor') === 'true') {
            headers['visual-editor'] = urlParams.get('visual_editor');
          }
        }
      }
    }

    // Return the headers to the context so httpLink can read them.
    return {
      headers,
    };
  });

  return new ApolloClient({
    connectToDevTools: process.browser,
    ssrMode: !process.browser, // Disables forceFetch on the server (so queries are only run once)
    link: ApolloLink.from([
      authLink.concat(
        ApolloLink.split(
          ({ variables }) => hasFiles(variables),
          createUploadLink({ uri: URL }),
          batchLink,
        ),
      ),
    ]),
    cache: new InMemoryCache({
      // possibleTypes: introspectionQueryResultData,
    }).restore(initialState || {}),
  });
}

export default function initApollo(initialState, ctx = {}, session = null) {
  // If ctx.apolloClient exists use that (from withApollo).
  if ((ctx as any).apolloClient) {
    return (ctx as any).apolloClient;
  }

  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (!process.browser) {
    return create(initialState, ctx, session);
  }

  // Reuse client on the client-side
  if (!apolloClient) {
    apolloClient = create(initialState, ctx, session);
  }

  return apolloClient;
}

export function addApolloState(client, pageProps) {
  if (pageProps?.props) {
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
}

export function useApollo(pageProps, locale, session) {
  const state = pageProps[APOLLO_STATE_PROP_NAME];
  const store = useMemo(
    () =>
      initApollo(
        state,
        {
          locale: locale,
        },
        session,
      ),
    [state],
  );
  return store;
}
